import type { LocalPoint } from "@excalidraw/math";

import type {
  ExcalidrawElement,
  ExcalidrawFreeDrawElement,
} from "@excalidraw/element/types";

export const FIREBASE_SCENE_ENCODING = "binary-freedraw-v2" as const;
export const LEGACY_FIREBASE_SCENE_ENCODING = "compact-freedraw-v1" as const;

export type FirebaseSceneEncoding =
  | typeof FIREBASE_SCENE_ENCODING
  | typeof LEGACY_FIREBASE_SCENE_ENCODING;

const LEGACY_COORDINATE_SCALE = 64;
const LEGACY_PRESSURE_SCALE = 255;
const MAX_POINTS_PER_STROKE = 1_000_000;
const FLOAT64_BYTES = 8;

type BinaryFreeDrawElement = Omit<
  ExcalidrawFreeDrawElement,
  "points" | "pressures"
> & {
  points: string;
  pressures: string;
  _pointCount: number;
  _pressureCount: number;
};

const bytesToBase64 = (bytes: Uint8Array): string => {
  const chunks: string[] = [];
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    chunks.push(
      String.fromCharCode(...bytes.subarray(offset, offset + chunkSize)),
    );
  }
  return btoa(chunks.join(""));
};

const base64ToBytes = (base64: string): Uint8Array => {
  const binary = atob(base64);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
};

const encodeFloat64Values = (values: readonly number[]): string | null => {
  const bytes = new Uint8Array(values.length * FLOAT64_BYTES);
  const view = new DataView(bytes.buffer);
  for (let index = 0; index < values.length; index++) {
    const value = values[index];
    if (!Number.isFinite(value)) {
      return null;
    }
    view.setFloat64(index * FLOAT64_BYTES, value, true);
  }
  return bytesToBase64(bytes);
};

const decodeFloat64Values = (encoded: string, valueCount: number): number[] => {
  if (
    !Number.isSafeInteger(valueCount) ||
    valueCount < 0 ||
    valueCount > MAX_POINTS_PER_STROKE * 2
  ) {
    throw new Error("Invalid binary free-draw value count");
  }

  const bytes = base64ToBytes(encoded);
  if (bytes.length !== valueCount * FLOAT64_BYTES) {
    throw new Error("Binary free-draw data length does not match value count");
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const values: number[] = [];
  for (let index = 0; index < valueCount; index++) {
    const value = view.getFloat64(index * FLOAT64_BYTES, true);
    if (!Number.isFinite(value)) {
      throw new Error("Binary free-draw value is not finite");
    }
    values.push(value);
  }
  return values;
};

const encodeFreeDrawElement = (
  element: ExcalidrawFreeDrawElement,
): ExcalidrawFreeDrawElement | BinaryFreeDrawElement => {
  const encodedPoints = encodeFloat64Values(element.points.flat());
  const encodedPressures = encodeFloat64Values(element.pressures);
  if (encodedPoints === null || encodedPressures === null) {
    return element;
  }

  const { points: _points, pressures: _pressures, ...metadata } = element;
  return {
    ...metadata,
    points: encodedPoints,
    pressures: encodedPressures,
    _pointCount: element.points.length,
    _pressureCount: element.pressures.length,
  };
};

const zigZagDecode = (value: number): number =>
  value % 2 === 0 ? value / 2 : -(value + 1) / 2;

const readLegacyVarint = (
  bytes: Uint8Array,
  cursor: { value: number },
): number => {
  let value = 0;
  let multiplier = 1;
  for (let byteIndex = 0; byteIndex < 8; byteIndex++) {
    if (cursor.value >= bytes.length) {
      throw new Error("Invalid compact free-draw point data");
    }
    const byte = bytes[cursor.value++];
    value += (byte & 0x7f) * multiplier;
    if (!Number.isSafeInteger(value)) {
      throw new Error("Compact free-draw coordinate is out of range");
    }
    if ((byte & 0x80) === 0) {
      return zigZagDecode(value);
    }
    multiplier *= 0x80;
  }
  throw new Error("Compact free-draw varint is too long");
};

const decodeLegacyPoints = (
  encoded: string,
  pointCount: number,
): LocalPoint[] => {
  if (
    !Number.isSafeInteger(pointCount) ||
    pointCount < 0 ||
    pointCount > MAX_POINTS_PER_STROKE
  ) {
    throw new Error("Invalid compact free-draw point count");
  }

  const bytes = base64ToBytes(encoded);
  const cursor = { value: 0 };
  const points: LocalPoint[] = [];
  let previousX = 0;
  let previousY = 0;
  for (let index = 0; index < pointCount; index++) {
    previousX += readLegacyVarint(bytes, cursor);
    previousY += readLegacyVarint(bytes, cursor);
    points.push([
      previousX / LEGACY_COORDINATE_SCALE,
      previousY / LEGACY_COORDINATE_SCALE,
    ] as LocalPoint);
  }
  if (cursor.value !== bytes.length) {
    throw new Error("Compact free-draw point data has trailing bytes");
  }
  return points;
};

const decodeLegacyPressures = (
  encoded: string,
  pointCount: number,
): number[] => {
  const bytes = base64ToBytes(encoded);
  if (bytes.length !== 0 && bytes.length !== pointCount) {
    throw new Error("Compact free-draw pressure count does not match points");
  }
  return Array.from(bytes, (pressure) => pressure / LEGACY_PRESSURE_SCALE);
};

export const isFirebaseSceneEncoding = (
  value: unknown,
): value is FirebaseSceneEncoding =>
  value === FIREBASE_SCENE_ENCODING || value === LEGACY_FIREBASE_SCENE_ENCODING;

export const encodeSceneElementsForStorage = (
  elements: readonly ExcalidrawElement[],
): readonly unknown[] =>
  elements.map((element) =>
    element.type === "freedraw" ? encodeFreeDrawElement(element) : element,
  );

export const decodeSceneElementsFromStorage = (
  storedElements: unknown,
  encoding: FirebaseSceneEncoding = FIREBASE_SCENE_ENCODING,
): readonly ExcalidrawElement[] => {
  if (!Array.isArray(storedElements)) {
    throw new Error("Invalid encoded scene data");
  }

  return storedElements.map((storedElement) => {
    if (
      !storedElement ||
      typeof storedElement !== "object" ||
      !("type" in storedElement) ||
      storedElement.type !== "freedraw" ||
      !("points" in storedElement) ||
      typeof storedElement.points !== "string"
    ) {
      return storedElement as ExcalidrawElement;
    }
    if (
      !("pressures" in storedElement) ||
      typeof storedElement.pressures !== "string" ||
      !("_pointCount" in storedElement) ||
      typeof storedElement._pointCount !== "number"
    ) {
      throw new Error("Invalid encoded free-draw element");
    }

    const {
      points: encodedPoints,
      pressures: encodedPressures,
      _pointCount: pointCount,
      _pressureCount: pressureCount,
      ...metadata
    } = storedElement;

    if (encoding === LEGACY_FIREBASE_SCENE_ENCODING) {
      return {
        ...metadata,
        points: decodeLegacyPoints(encodedPoints, pointCount),
        pressures: decodeLegacyPressures(encodedPressures, pointCount),
      } as ExcalidrawFreeDrawElement;
    }

    if (typeof pressureCount !== "number") {
      throw new Error("Invalid binary free-draw pressure count");
    }
    const pointValues = decodeFloat64Values(encodedPoints, pointCount * 2);
    const points: LocalPoint[] = [];
    for (let index = 0; index < pointValues.length; index += 2) {
      points.push([pointValues[index], pointValues[index + 1]] as LocalPoint);
    }

    return {
      ...metadata,
      points,
      pressures: decodeFloat64Values(encodedPressures, pressureCount),
    } as ExcalidrawFreeDrawElement;
  });
};
